import { Haptics, NotificationType } from "@capacitor/haptics"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import {
  ExpenseCategory,
  ExpensesErrors
} from "@/types/expense"

import { ExpensesForm } from "./ExpensesCreatePage"

import { PiArrowLeft, PiTrash } from "react-icons/pi"

import QuickActionsModal from "@/components/dashboard/QuickActionsModal/QuickActionsModal"

import expenseAPI from "@/services/api/expense"

import { useExpensesFormStore } from "@/store/useExpensesFormStore"
import { useDashboardStore } from "@/store/useDashboardStore"
import { usePageSetup } from "@/hooks/usePageSetup"

export const ExpensesUpdatePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('expenses.pageTitle.edit'))
  const navigate = useNavigate()

  const defaultForm = useExpensesFormStore((state) => state.form)

  const sgid = defaultForm.sgid || ''

  const defaultCategory = defaultForm.category || '' as ExpenseCategory
  const defaultTotal = defaultForm.total || 0
  const defaultOperation = defaultForm.operation
  const defaultPercentage = defaultForm.percentage || 100
  const defaultDate = defaultForm.date
  const defaultReference = defaultForm.reference

  const [category, setCategory] = useState<ExpenseCategory>(defaultCategory)
  const [total, setTotal] = useState<number>(defaultTotal)
  const [percentage, setPercentage] = useState<number>(defaultPercentage)

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<ExpensesErrors>({})

  const financialYear = useDashboardStore((state) => state.financialYear)

  const onDateComplete = (date: string) => {
    const year = date.split('-')[0]

    if (year !== financialYear.toString()) {
      setErrors({ date: [t('validation.yearMismatch')] })
    } else {
      setErrors((prev) => {
        const { date, ...rest } = prev
        return rest
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    try {
      const form = new FormData(e.currentTarget)

      const date = form.get('date') as string
      const cents = Math.round(total * 100)
      const reference = form.get('reference') as string
      const operation = form.get('operation') as string

      if (cents < 0) {
        setIsLoading(false)
        return
      }

      await expenseAPI.update(sgid, {
        expense_category_code: category,
        amount_cents: cents,
        percentage: percentage,
        date,
        operation,
        reference
      })

      navigate("/expenses")

      document.getElementById('root')?.scrollTo(0, 0);
      Haptics.notification({ type: NotificationType.Success })
    } catch (e: any) {
      const errors = e.response?.data?.errors

      if (errors?.reference) {
        setErrors({ reference: [t('validation.referenceExists')] })
      }

      setIsLoading(false)
    }
  }

  const deleteActions = [
    {
      icon: <div className='w-10 h-10 flex items-center justify-center bg-red-300 rounded-full'>
        <PiTrash className="w-5 h-5 text-[#1D2233]" />
      </div>,
      text: t('expenses.confirmDelete'),
      subtext: t('expenses.deleteWarning'),
      onClick: () => expenseAPI.delete(sgid).then(() => navigate("/expenses")),
      className: "rounded-t-3xl rounded-b-xl"
    },
    {
      icon: <div className='w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full'>
        <PiArrowLeft className="w-5 h-5 text-[#1D2233]" />
      </div>,
      text: t('profile.goBack'),
      subtext: "",
      onClick: () => setDeleteConfirmOpen(false),
      className: "rounded-b-3xl rounded-t-xl"
    },
  ];

  return <>
    <QuickActionsModal
      actions={deleteActions}
      isOpen={deleteConfirmOpen}
      setOpen={setDeleteConfirmOpen}
    />
    <ExpensesForm
      title={t('expenses.edit')}
      handleDelete={async () => setDeleteConfirmOpen(true)}
      handleSubmit={handleSubmit}

      isLoading={isLoading}

      file={undefined}
      defaultCategory={defaultCategory}
      category={category}
      defaultOperation={defaultOperation}
      defaultTotal={defaultTotal}
      total={total}
      defaultPercentage={defaultPercentage}
      percentage={percentage}
      defaultDate={defaultDate}
      defaultReference={defaultReference}

      setTotal={setTotal}
      setPercentage={setPercentage}
      setCategory={setCategory}

      onDateComplete={onDateComplete}
      errors={errors}
    />
  </>
}

export default ExpensesUpdatePage